/**
 * Permission Admin Service (Frontend)
 *
 * HTTP client for the permission admin API. Supports listing operations
 * and mappings, creating/deleting mappings, and cache management.
 */
import { getBackendUrl } from './configService';

const getApiBaseUrl = (): string => {
  const url = getBackendUrl();
  if (!url) {
    throw new Error('BACKEND_URL not configured in runtime config');
  }
  return url;
};

function getAuthHeaders(): HeadersInit {
  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('access_token');
  if (token && token !== 'dev-mode-token') {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

// ─── Types ──────────────────────────────────────────────────────────────────

export interface FeatureOperation {
  id?: string;
  feature: string;
  operation: string;
  description: string | null;
  operationType: 'read' | 'mutation';
  uiHint: string | null;
  routeHint: string | null;
  isActive: boolean;
}

export interface PermissionMapping {
  id: string;
  permission: string;
  feature: string;
  operation: string;
  granted_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CacheInfo {
  loadedAt: string;
  permissionCount: number;
  totalMappings: number;
}

// ─── API calls ──────────────────────────────────────────────────────────────

export async function fetchOperations(): Promise<FeatureOperation[]> {
  const url = `${getApiBaseUrl()}/api/permissions/operations`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch operations: HTTP ${response.status}`);
  }

  const json = await response.json();
  return json.operations;
}

export async function fetchMappings(): Promise<PermissionMapping[]> {
  const url = `${getApiBaseUrl()}/api/permissions/mappings`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch mappings: HTTP ${response.status}`);
  }

  const json = await response.json();
  return json.mappings;
}

export async function createMapping(
  permission: string,
  feature: string,
  operation: string,
): Promise<PermissionMapping> {
  const url = `${getApiBaseUrl()}/api/permissions/mappings`;
  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({ permission, feature, operation }),
  });

  if (response.status === 409) {
    throw new Error('Mapping already exists');
  }
  if (response.status === 400) {
    const json = await response.json();
    throw new Error(json.error || 'Invalid mapping');
  }
  if (!response.ok) {
    throw new Error(`Failed to create mapping: HTTP ${response.status}`);
  }

  const json = await response.json();
  return json.mapping;
}

export async function deleteMapping(id: string): Promise<void> {
  const url = `${getApiBaseUrl()}/api/permissions/mappings/${encodeURIComponent(id)}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to delete mapping: HTTP ${response.status}`);
  }
}

export async function fetchCacheInfo(): Promise<CacheInfo> {
  const url = `${getApiBaseUrl()}/api/permissions/cache/info`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch cache info: HTTP ${response.status}`);
  }

  const json = await response.json();
  return json.cache;
}

// ─── Import / Export ────────────────────────────────────────────────────────

export interface PermissionExport {
  version: 1;
  exported_at: string;
  mappings: { permission: string; feature: string; operation: string }[];
}

export interface BulkImportResult {
  created: number;
  skipped: number;
  errors: { permission: string; feature: string; operation: string; reason: string }[];
}

/**
 * Export all mappings as a JSON file download.
 * Pure client-side — no new backend endpoint needed.
 */
export async function exportMappings(): Promise<void> {
  const mappings = await fetchMappings();

  const payload: PermissionExport = {
    version: 1,
    exported_at: new Date().toISOString(),
    mappings: mappings.map(m => ({
      permission: m.permission,
      feature: m.feature,
      operation: m.operation,
    })),
  };

  const json = JSON.stringify(payload, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const date = new Date().toISOString().slice(0, 10);
  const filename = `em3_permissions_${date}.json`;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * Bulk import mappings via POST /api/permissions/mappings/bulk.
 */
export async function bulkImportMappings(
  mappings: { permission: string; feature: string; operation: string }[],
): Promise<BulkImportResult> {
  const url = `${getApiBaseUrl()}/api/permissions/mappings/bulk`;
  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({ mappings }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Bulk import failed: HTTP ${response.status} — ${text}`);
  }

  return response.json();
}

export async function rebuildCache(): Promise<CacheInfo> {
  const url = `${getApiBaseUrl()}/api/permissions/cache/rebuild`;
  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to rebuild cache: HTTP ${response.status}`);
  }

  const json = await response.json();
  return json.cache;
}
